import React from 'react';
import { useAPIContext } from './api';
import { Spin } from 'antd';
import { SettingsAPI } from '../../../constructs/settings/interface';
import { BrandsSettings } from '../types/brands-settings';
import { useAuthContext } from './auth';

export interface SettingsState {
  settings: BrandsSettings;
}

const SettingsContext = React.createContext<SettingsState | null>(null);

export const SettingsProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { callEndpoint } = useAPIContext();
  const { isLoggedIn, loading: authLoading } = useAuthContext();
  const hasInitialized = React.useRef(false);

  const [settings, setSettings] = React.useState<BrandsSettings | null>(null);

  React.useEffect(() => {
    if (hasInitialized.current) return;

    if (!isLoggedIn || authLoading) {
      return;
    }

    const fetchSettings = async () => {
      try {
        const response = await callEndpoint<SettingsAPI, 'get-settings'>('settings', '/BRANDS', 'GET');
        setSettings((response as BrandsSettings) || {});
      } catch (error) {
        console.error('Error fetching settings:', error);
        setSettings({});
      }
    };

    hasInitialized.current = true;
    fetchSettings();
  }, [callEndpoint, isLoggedIn, authLoading]);

  if (authLoading || settings === null) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', width: '100vw' }}>
        <Spin size='large' />
      </div>
    );
  }

  return <SettingsContext.Provider value={{ settings }}>{children}</SettingsContext.Provider>;
};

export const useSettingsContext = () => {
  const context = React.useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettingsContext must be used within SettingsProvider');
  }
  return context;
};
